/**
 * Mapa del conglomerado.
 *
 * Muestra el polígono del área asignada, la posición del encuestador y las
 * viviendas ya visitadas. Sirve para orientarse en el recorrido por manzana de
 * §5.3.2, que en el rural (La Rumorosa, El Hongo, Jácume) es imposible de
 * seguir sin referencia visual.
 *
 * TESELAS AUTOALOJADAS: el estilo apunta a un PMTiles servido desde DO Spaces,
 * sin dependencia de terceros ni límites de uso, de modo que el precacheo para
 * trabajar sin señal sea legítimo. Si la URL no está configurada, el mapa
 * dibuja los polígonos sobre fondo vacío: menos útil para orientarse, pero
 * funcional, y es mejor que una pantalla en blanco.
 */

import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';

import 'maplibre-gl/dist/maplibre-gl.css';

import { MAP_LOCALE, buildBaseStyle, registerPmtiles, tilesUrl } from './pmtiles-protocol';
import type { GeoJsonFeatureCollection } from '../api/client';

export interface MapMarker {
  longitude: number;
  latitude: number;
  kind: 'interview' | 'refusal' | 'no-contact' | 'current';
  label?: string;
}

export interface ClusterMapProps {
  /** Polígono del conglomerado o de la sección. */
  area: GeoJsonFeatureCollection | null;
  /** Posición en vivo del encuestador. */
  position: { latitude: number; longitude: number; accuracyMeters: number | null } | null;
  markers?: MapMarker[];
  /** Punto que se está capturando, para verlo contra el polígono. */
  candidate?: { latitude: number; longitude: number } | null;
  full?: boolean;
}

export function ClusterMap({ area, position, markers = [], candidate, full }: ClusterMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const [failed, setFailed] = useState(false);

  /* ------------------------------ Creación ------------------------------ */

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    try {
      registerPmtiles();

      const style = buildBaseStyle();

      const map = new maplibregl.Map({
        container: containerRef.current,
        style,
        center: [-116.63, 32.56],
        zoom: 13,
        // El tileset cubre z8–z16. Permitir salir de ese rango deja el mapa en
        // blanco, que para quien lo usa es indistinguible de un fallo.
        minZoom: 8,
        maxZoom: 16,
        /**
         * El scroll NO hace zoom por omisión.
         *
         * Ambos mapas viven dentro de páginas que se desplazan, y con el zoom
         * por rueda activo el cursor sobre el mapa secuestra el scroll de la
         * página: el usuario intenta bajar y el mapa se aleja hasta quedarse
         * sin teselas. Con gestos cooperativos hace falta Ctrl/Cmd + rueda
         * para hacer zoom, o dos dedos en táctil, y MapLibre muestra la
         * indicación cuando corresponde.
         */
        cooperativeGestures: true,
        locale: MAP_LOCALE,
        attributionControl: false,
        // Sin rotación: en campo el gesto de dos dedos gira el mapa por
        // accidente y desorienta.
        pitchWithRotate: false,
        dragRotate: false,
        touchZoomRotate: true,
      });

      map.touchZoomRotate.disableRotation();
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(
        new maplibregl.AttributionControl({
          compact: true,
          customAttribution: '© OpenStreetMap · Cartografía electoral: INE',
        }),
        'bottom-right',
      );

      /**
       * MapLibre emite `error` para cosas NO fatales: una tesela que falta en
       * un borde, un recurso opcional, una petición cancelada al navegar.
       * Tratarlas como fatales ocultaría un mapa que funciona.
       */
      map.on('error', (event) => {
        console.warn('[mapa]', event?.error?.message ?? String(event?.error ?? 'error'));
      });

      /**
       * Red de seguridad por si el mapa nunca llega a cargar.
       *
       * El temporizador se guarda en una ref y SE CANCELA EN LA LIMPIEZA del
       * efecto. Sin eso, el modo estricto de React —que monta, desmonta y
       * vuelve a montar— dejaba vivo el temporizador de la PRIMERA instancia,
       * ya destruida: su `load` nunca llegaba, así que a los 12 segundos
       * declaraba fallido el mapa que sí estaba funcionando. El mapa se veía
       * bien y desaparecía solo, pareciendo culpa de cualquier cosa que el
       * usuario estuviera haciendo en ese momento.
       *
       * Se usa el evento `load` y no `isStyleLoaded()`, que devuelve false de
       * forma transitoria mientras se cargan teselas tras un zoom.
       */
      let loaded = false;
      map.on('load', () => {
        loaded = true;
        if (timeoutRef.current !== null) {
          window.clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      });
      timeoutRef.current = window.setTimeout(() => {
        if (!loaded) setFailed(true);
      }, 15_000);

      mapRef.current = map;
    } catch (error) {
      console.error('[mapa] no se pudo crear:', error);
      setFailed(true);
    }

    return () => {
      // Cancelar el temporizador es parte de la limpieza, no un detalle: es
      // justo lo que faltaba.
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  /* ------------------------- Polígono del área -------------------------- */

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !area) return;

    const apply = () => {
      const source = map.getSource('area') as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(area as never);
      } else {
        map.addSource('area', { type: 'geojson', data: area as never });
        map.addLayer({
          id: 'area-relleno',
          type: 'fill',
          source: 'area',
          paint: { 'fill-color': '#1d4ed8', 'fill-opacity': 0.1 },
        });
        map.addLayer({
          id: 'area-borde',
          type: 'line',
          source: 'area',
          paint: { 'line-color': '#1d4ed8', 'line-width': 3 },
        });
      }

      // Encuadra el área asignada al cargarla.
      const bounds = new maplibregl.LngLatBounds();
      let hasPoints = false;
      for (const feature of area.features) {
        const polygons =
          feature.geometry.type === 'Polygon'
            ? [feature.geometry.coordinates as number[][][]]
            : (feature.geometry.coordinates as number[][][][]);
        for (const polygon of polygons) {
          for (const ring of polygon) {
            for (const coordinate of ring) {
              bounds.extend([coordinate[0]!, coordinate[1]!]);
              hasPoints = true;
            }
          }
        }
      }
      if (hasPoints) map.fitBounds(bounds, { padding: 32, maxZoom: 16, duration: 0 });
    };

    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [area]);

  /* ------------------------ Posición y marcadores ----------------------- */

  const positionMarkerRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !position) return;

    if (!positionMarkerRef.current) {
      const element = document.createElement('div');
      element.style.cssText =
        'width:18px;height:18px;border-radius:50%;background:#1d4ed8;border:3px solid #fff;box-shadow:0 0 0 2px rgba(29,78,216,.35)';
      positionMarkerRef.current = new maplibregl.Marker({ element })
        .setLngLat([position.longitude, position.latitude])
        .addTo(map);
    } else {
      positionMarkerRef.current.setLngLat([position.longitude, position.latitude]);
    }
  }, [position]);

  const markerRefs = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    for (const marker of markerRefs.current) marker.remove();
    markerRefs.current = [];

    const colors: Record<MapMarker['kind'], string> = {
      interview: '#15803d',
      refusal: '#b91c1c',
      'no-contact': '#b45309',
      current: '#1d4ed8',
    };

    for (const marker of markers) {
      const element = document.createElement('div');
      element.style.cssText = `width:13px;height:13px;border-radius:50%;background:${colors[marker.kind]};border:2px solid #fff`;
      const created = new maplibregl.Marker({ element })
        .setLngLat([marker.longitude, marker.latitude])
        .addTo(map);
      if (marker.label) {
        created.setPopup(new maplibregl.Popup({ offset: 12 }).setText(marker.label));
      }
      markerRefs.current.push(created);
    }
  }, [markers]);

  const candidateRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    candidateRef.current?.remove();
    candidateRef.current = null;
    if (!candidate) return;

    const element = document.createElement('div');
    element.style.cssText =
      'width:22px;height:22px;border-radius:50%;background:#f59e0b;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)';
    candidateRef.current = new maplibregl.Marker({ element })
      .setLngLat([candidate.longitude, candidate.latitude])
      .addTo(map);
    map.easeTo({ center: [candidate.longitude, candidate.latitude], zoom: 17, duration: 300 });
  }, [candidate]);

  if (failed) {
    return (
      <div className={`map${full ? ' map--full' : ''}`}>
        <div className="map__placeholder">
          No se pudo cargar el mapa. Puedes seguir capturando: la validación de la ubicación no
          depende del mapa.
        </div>
      </div>
    );
  }

  return (
    <div className={`map${full ? ' map--full' : ''}`}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

/** Aviso cuando no hay teselas configuradas. */
export function TilesWarning() {
  if (tilesUrl()) return null;
  return (
    <p className="faint" style={{ marginTop: 8 }}>
      Sin teselas configuradas: el mapa muestra el polígono sobre fondo vacío, sin calles.
    </p>
  );
}
